import React, { useCallback, useState } from 'react';

import { GX_TEXT } from '@/config/colors';

import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import FileDownloadDoneOutlined from '@mui/icons-material/FileDownloadDoneOutlined';
import MoreVert from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@/shared/ui';

import AddApp from '@/assets/icons/AddApp';
import { Z_INDEX } from '@/config/z-index';
import { ButtonsContainer } from '@/features/dashboard/components/Drawer/components/ButtonsContainer';
import { DASHBOARD_ACTION_BUTTON_WIDTH } from '@/features/dashboard/constants/style';
import Button from '@/shared/ui/primitives/Button';

type EditActionButtonsProps = {
  isPending: boolean;
  isSaveDisabled: boolean;
  onDiscard: () => void;
  onAddApp: () => void;
  onSave: () => void;
  onRemoveAll: () => void;
  onRemoveDashboard: () => void;
};

const moreIconBtnSx = {
  height: '48px',
  color: 'gx.text',
  backgroundColor: 'gx.tileLight',
  borderRadius: '0',
  width: '48px'
};
const menuSx = { zIndex: Z_INDEX.menu };
const menuItemRowSx = { display: 'flex', alignItems: 'center', gap: 1 };
const menuIconSx = { fontSize: '20px' };
const actionBtnSx = { width: DASHBOARD_ACTION_BUTTON_WIDTH };
const addAppIconSx = { width: '24px', height: '24px' };
const saveIconSx = { fontSize: '24px !important' };

export const EditActionButtons = ({
  isPending,
  isSaveDisabled,
  onDiscard,
  onAddApp,
  onSave,
  onRemoveAll,
  onRemoveDashboard
}: EditActionButtonsProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleRemoveAllClick = useCallback(() => {
    onRemoveAll();
    handleMenuClose();
  }, [handleMenuClose, onRemoveAll]);

  const handleRemoveDashboardClick = useCallback(() => {
    onRemoveDashboard();
    handleMenuClose();
  }, [handleMenuClose, onRemoveDashboard]);

  return (
    <ButtonsContainer>
      <Tooltip title='More options'>
        <IconButton
          color='secondary'
          disabled={isPending}
          sx={moreIconBtnSx}
          onClick={handleMenuOpen}
        >
          <MoreVert />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menuAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        open={isMenuOpen}
        sx={menuSx}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleRemoveAllClick}>
          <Box sx={menuItemRowSx}>
            <DeleteOutlineIcon sx={menuIconSx} />
            Remove all
          </Box>
        </MenuItem>
        <MenuItem onClick={handleRemoveDashboardClick}>
          <Box sx={menuItemRowSx}>
            <DeleteForeverOutlinedIcon sx={menuIconSx} />
            Delete dashboard
          </Box>
        </MenuItem>
      </Menu>
      <Button
        color='secondary'
        disabled={isPending}
        size='large'
        sx={actionBtnSx}
        variant='contained'
        onClick={onDiscard}
      >
        Discard
      </Button>
      <Button
        color='secondary'
        size='large'
        startIcon={
          <AddApp
            fill={GX_TEXT}
            sx={addAppIconSx}
          />
        }
        sx={actionBtnSx}
        variant='contained'
        onClick={onAddApp}
      >
        Add app
      </Button>
      <Button
        disabled={isSaveDisabled}
        size='large'
        startIcon={<FileDownloadDoneOutlined sx={saveIconSx} />}
        sx={actionBtnSx}
        variant='contained'
        onClick={onSave}
      >
        Save
      </Button>
    </ButtonsContainer>
  );
};
